/**
 * LoadingScreen — full-screen overlay shown while Phaser / BootScene loads assets.
 *
 * WHY a separate React component instead of relying on the Phaser canvas bar:
 * The Phaser canvas is invisible until the WebGL context initialises (~200ms).
 * During that gap the user sees a blank black screen. This component fills that gap
 * with a React-rendered bar that appears immediately, driven by useGameStore.isLoading.
 *
 * Lifecycle: BootScene calls setLoading(pct, msg) on Phaser's 'progress' event
 * and setLoadingDone() after textures are generated. isLoading: false triggers
 * a CSS fade-out before the component unmounts.
 *
 * The barFill width is computed inline (not in the styles record) to avoid the
 * TypeScript error caused by storing a function in a CSSProperties record.
 */

import React, { useEffect, useRef, useState } from 'react'

interface LoadingScreenProps {
  progress: number
  message: string
}

const keyframes = `
@keyframes ultimaze-pulse {
  0%, 100% { opacity: 1; text-shadow: 0 0 20px rgba(255, 215, 0, 0.6); }
  50% { opacity: 0.7; text-shadow: 0 0 40px rgba(255, 215, 0, 1); }
}
@keyframes loadingFadeOut {
  from { opacity: 1; }
  to   { opacity: 0; pointer-events: none; }
}
`

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ progress, message }) => {
  // Track whether we are in the fade-out phase (progress reached 100).
  // The fade takes 400ms; the parent should unmount us shortly after.
  const [fadingOut, setFadingOut] = useState(false)
  const didFadeRef = useRef(false)

  useEffect(() => {
    if (progress >= 100 && !didFadeRef.current) {
      didFadeRef.current = true
      setFadingOut(true)
    }
  }, [progress])

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#0A0810',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    animation: fadingOut ? 'loadingFadeOut 0.4s ease forwards' : undefined,
  }

  const barFillStyle: React.CSSProperties = {
    height: '100%',
    width: `${Math.min(100, Math.max(0, progress))}%`,
    backgroundColor: '#FFD700',
    borderRadius: '4px',
    transition: 'width 0.25s ease-out',
  }

  return (
    <>
      <style>{keyframes}</style>
      <div style={overlayStyle}>
        <div style={styles.logo}>ULTIMAZE</div>
        <div style={styles.barTrack}>
          <div style={barFillStyle} />
        </div>
        <div style={styles.message}>{message}</div>
        <div style={styles.pct}>{Math.round(progress)}%</div>
      </div>
    </>
  )
}

const styles: Record<string, React.CSSProperties> = {
  logo: {
    fontSize: '48px',
    fontWeight: 900,
    color: '#FFD700',
    letterSpacing: '6px',
    textTransform: 'uppercase',
    animation: 'ultimaze-pulse 2s ease-in-out infinite',
    marginBottom: '48px',
    fontFamily: 'system-ui, sans-serif',
    userSelect: 'none',
  },
  barTrack: {
    width: '280px',
    height: '8px',
    backgroundColor: '#1E1830',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  message: {
    color: '#8A7FA0',
    fontSize: '14px',
    fontFamily: 'system-ui, sans-serif',
    letterSpacing: '1px',
    userSelect: 'none',
    marginBottom: '4px',
  },
  pct: {
    color: '#555',
    fontSize: '12px',
    fontFamily: 'monospace',
    userSelect: 'none',
  },
}

export default LoadingScreen
